import range from 'lodash/range';

import { GetOrdersResponse } from '@/app/utils/getOrders';
import { OrderStatus } from '@/types/Order';

import { address as shippingAddressMock } from './address';
import { SubmitOrderResponse } from '@/app/utils/submitOrder';
import {
  COBRANDED_PARTNER_IDS,
  CobrandedPartnerName,
} from '@/app/configs/cobrandedPartners';
import { GetEligibilitiesResponse } from '@/app/utils/getUnclaimedEligibilities';
import { ValidateRedeemCodeResult } from '../src/queries/Redeem';
import { EligibilityItem } from '@/app/api/getEligibilities/types';

const UNENROLLED_SHIPPING_ADDRESS = {
  ...shippingAddressMock,
  address: '',
  address2: '',
  city: '',
  state: '',
  postal: '',
};

const mockDate = new Date().toISOString();

export type Mock = {
  submit: SubmitOrderResponse;
  getOrders: GetOrdersResponse;
  getEligibilities?: GetEligibilitiesResponse;
};

const submitMocks = {
  SUBMIT_SUCCESS: {
    status: 'success',
    error: undefined,
  },
  SUBMIT_ERROR: {
    error: 'Error',
  },
};

const getOrdersMocks = {
  MULTIPLE_ORDERS: [
    {
      salesOrderId: '136db711-ce67-4927-9c48-4b07444d31c8',
      netsuiteOrderId: 'SO-123456',
      orderStatus: OrderStatus.pending,
      lineItems: [
        {
          originalSku: 'JZ90-4300',
          quantity: 1,
        },
        {
          originalSku: 'JZ90-4300',
          quantity: 1,
        },
        {
          originalSku: 'JZSE-0167',
          quantity: 1,
        },
      ],
      shippingAddress: shippingAddressMock,
      orderDate: mockDate,
    },
    {
      salesOrderId: '136db711-ce67-4927-9c48-4b07444d31c8',
      netsuiteOrderId: 'SO-123457',
      orderStatus: OrderStatus.pending,
      lineItems: [
        {
          originalSku: 'JZ90-4300',
          quantity: 1,
        },
        {
          originalSku: 'JZSE-0167',
          quantity: 1,
        },
      ],
      shippingAddress: shippingAddressMock,
      orderDate: mockDate,
    },
    {
      salesOrderId: '136db711-ce67-4927-9c48-4b07444d31c8',
      netsuiteOrderId: 'SO-123460',
      orderStatus: OrderStatus.pending,
      lineItems: [
        // 22 "selected" rings
        ...range(22).map(() => ({
          originalSku: 'JZ90-4300',
          sku: 'JZ90-54144-11',
          quantity: 1,
        })),
        {
          originalSku: 'JZSE-0167',
          quantity: 1,
        },
      ],
      shippingAddress: shippingAddressMock,
      orderDate: mockDate,
    },
    {
      salesOrderId: '136db711-ce67-4927-9c48-4b07444d31c8',
      netsuiteOrderId: 'SO-123450',
      orderStatus: OrderStatus.pending,
      lineItems: [
        {
          originalSku: 'JZ90-4300',
          quantity: 1,
        },
        {
          originalSku: 'JZ98-53986',
          quantity: 1,
        },
        {
          originalSku: 'JZSE-0167',
          quantity: 1,
        },
      ],
      shippingAddress: UNENROLLED_SHIPPING_ADDRESS,
      orderDate: mockDate,
    },
    {
      salesOrderId: '136db711-ce67-4927-9c48-4b07444d31c8',
      netsuiteOrderId: 'SO-123451',
      orderStatus: OrderStatus.pending,
      lineItems: [
        {
          originalSku: 'JZ90-4300',
          quantity: 1,
        },
        {
          originalSku: 'JZ90-4300',
          quantity: 1,
        },
        {
          originalSku: 'JZ98-53986',
          quantity: 1,
        },
        {
          originalSku: 'JZSE-0167',
          quantity: 1,
        },
      ],
      shippingAddress: UNENROLLED_SHIPPING_ADDRESS,
      orderDate: mockDate,
    },
  ],
  MULTIPLE_CHANNEL_ORDERS: [
    {
      salesOrderId: 'order-1',
      netsuiteOrderId: 'SO-100001',
      orderStatus: OrderStatus.pending,
      channel: '004-CIG',
      lineItems: [{ originalSku: 'JZ90-4300', quantity: 1 }],
      shippingAddress: shippingAddressMock,
      orderDate: mockDate,
    },
    {
      salesOrderId: 'order-2',
      netsuiteOrderId: 'SO-100002',
      orderStatus: OrderStatus.pending,
      channel: 'Cigna',
      lineItems: [{ originalSku: 'JZ90-4300', quantity: 1 }],
      shippingAddress: shippingAddressMock,
      orderDate: mockDate,
    },
    {
      salesOrderId: 'order-3',
      netsuiteOrderId: 'SO-100003',
      orderStatus: OrderStatus.pending,
      channel: 'nonCigna',
      lineItems: [{ originalSku: 'JZ90-4300', quantity: 1 }],
      shippingAddress: shippingAddressMock,
      orderDate: mockDate,
    },
    {
      salesOrderId: 'order-4',
      netsuiteOrderId: 'SO-100004',
      orderStatus: OrderStatus.pending,
      // no channel attribute
      lineItems: [{ originalSku: 'JZ90-4300', quantity: 1 }],
      shippingAddress: shippingAddressMock,
      orderDate: mockDate,
    },
  ],
  MULTIPLE_PAGES: [
    {
      salesOrderId: '136db711-ce67-4927-9c48-4b07444d31c8',
      netsuiteOrderId: 'SO-123459',
      orderStatus: OrderStatus.pending,
      lineItems: [
        // 11 "selected" rings
        ...range(11).map(() => ({
          originalSku: 'JZ90-4300',
          sku: 'JZ90-54144-11',
          quantity: 1,
        })),
        // 11 "incomplete" rings
        ...range(11).map(() => ({
          originalSku: 'JZ90-4300',
          quantity: 1,
        })),
        {
          originalSku: 'JZSE-0167',
          quantity: 1,
        },
      ],
      shippingAddress: shippingAddressMock,
      orderDate: mockDate,
    },
  ],
  SINGLE_ORDER: [
    {
      salesOrderId: '136db711-ce67-4927-9c48-4b07444d31c8',
      netsuiteOrderId: 'SO-123456',
      orderStatus: OrderStatus.pending,
      lineItems: [
        {
          originalSku: 'JZ90-4300',
          quantity: 1,
        },
        {
          originalSku: 'JZSE-0167',
          quantity: 1,
        },
      ],
      shippingAddress: shippingAddressMock,
      orderDate: mockDate,
    },
  ],
  SINGLE_COBRANDED_ORDER: [
    {
      salesOrderId: '136db711-ce67-4927-9c48-4b07444d31c8',
      netsuiteOrderId: 'SO-123456',
      orderStatus: OrderStatus.pending,
      lineItems: [
        {
          originalSku: 'JZ90-4300',
          quantity: 1,
        },
        {
          originalSku: 'JZSE-0167',
          quantity: 1,
        },
      ],
      shippingAddress: shippingAddressMock,
      orderDate: mockDate,
      channel: '004-CIG',
    },
  ],
  SINGLE_ORDER_MISSING_ORDERID: [
    {
      salesOrderId: '136db711-ce67-4927-9c48-4b07444d31c8',
      netsuiteOrderId: undefined,
      orderStatus: OrderStatus.pending,
      lineItems: [
        {
          originalSku: 'JZ90-4300',
          quantity: 1,
        },
        {
          originalSku: 'JZSE-0167',
          quantity: 1,
        },
      ],
      shippingAddress: shippingAddressMock,
      orderDate: mockDate,
    },
  ],
  COMPLETED_ORDER: [
    {
      salesOrderId: '136db711-ce67-4927-9c48-4b07444d31c8',
      netsuiteOrderId: 'SO-123456',
      orderStatus: OrderStatus.completed,
      lineItems: [
        {
          originalSku: 'JZ90-4300',
          sku: 'JZ90-54217-06',
          quantity: 1,
        },
        {
          originalSku: 'JZSE-0167',
          sku: 'JZSE-0167',
          quantity: 1,
        },
      ],
      shippingAddress: shippingAddressMock,
      orderDate: mockDate,
    },
    {
      salesOrderId: '136db711-ce67-4927-9c48-4b07444d31c8',
      netsuiteOrderId: 'SO-234567',
      orderStatus: OrderStatus.completed,
      lineItems: [
        {
          originalSku: 'JZ90-4300',
          sku: 'JZ90-54217-06',
          quantity: 1,
        },
        {
          originalSku: 'JZSE-0167',
          sku: 'JZSE-0167',
          quantity: 1,
        },
        {
          originalSku: 'JZ90-4300',
          sku: 'JZ90-54217-11',
          quantity: 1,
        },
        {
          originalSku: 'JZSE-0167',
          sku: 'JZSE-0167',
          quantity: 1,
        },
      ],
      shippingAddress: shippingAddressMock,
      orderDate: mockDate,
    },
  ],
  ENROLL: [
    {
      salesOrderId: '136db711-ce67-4927-9c48-4b07444d31c8',
      netsuiteOrderId: 'SO-123456',
      orderStatus: OrderStatus.pending,
      lineItems: [
        {
          originalSku: 'JZ90-4300',
          quantity: 1,
        },
        {
          originalSku: 'JZ98-53986',
          quantity: 1,
        },
        {
          originalSku: 'JZSE-0167',
          quantity: 1,
        },
      ],
      shippingAddress: UNENROLLED_SHIPPING_ADDRESS,
      orderDate: mockDate,
    },
  ],
  ENROLL_NO_SIZING: [
    {
      salesOrderId: '136db711-ce67-4927-9c48-4b07444d31c8',
      netsuiteOrderId: 'SO-123456',
      orderStatus: OrderStatus.pending,
      lineItems: [
        {
          originalSku: 'JZ90-4300',
          quantity: 1,
        },
        {
          originalSku: 'JZSE-0167',
          quantity: 1,
        },
      ],
      shippingAddress: UNENROLLED_SHIPPING_ADDRESS,
      orderDate: mockDate,
    },
  ],
};

const getEligibilitiesMocks: Record<string, EligibilityItem[]> = {
  SINGLE_UNCLAIMED: [
    {
      id: '5d89ca1a-353c-4031-a468-9c4e852af12a',
      status: 'unclaimed' as const,
      isEligible: true,
      customer: {
        id: 'Cust-1902427',
        name: 'Essence Customer',
      },
      shippingCountries: ['US'],
      dealId: 'test-deal-2',
      lineItems: [
        {
          title: 'Oura Ring 4 Black Size Later',
          quantity: 1,
          skuCode: 'JZ90-54144',
          productType: 'ring:style-later:core',
        },
        {
          title: 'Oura Ring 4 Silver Size Later',
          quantity: 1,
          skuCode: 'JZ90-54217',
          productType: 'ring:style-later:core',
        },
        {
          title: 'Oura Ring 4 Stealth Size Later',
          quantity: 1,
          skuCode: 'JZ90-54218',
          productType: 'ring:style-later:premium',
        },
        {
          title: 'Oura Ring 4 Sizing Kit RTL',
          quantity: 1,
          skuCode: 'JZ98-54867',
          productType: 'accessory',
        },
      ],
    },
  ],
};

function addChannel<T>(
  orders: T[],
  channel: string,
): (T & { channel: string })[] {
  return orders.map((order) => ({ ...order, channel }));
}

const getCignaMocks = {
  SINGLE_ORDER: addChannel(
    getOrdersMocks.SINGLE_ORDER,
    COBRANDED_PARTNER_IDS[CobrandedPartnerName.Cigna],
  ),
  MULTIPLE_ORDERS: addChannel(
    getOrdersMocks.MULTIPLE_ORDERS,
    COBRANDED_PARTNER_IDS[CobrandedPartnerName.Cigna],
  ),
  MULTI_CHANNEL_ORDERS: [
    ...addChannel(
      getOrdersMocks.SINGLE_ORDER,
      COBRANDED_PARTNER_IDS[CobrandedPartnerName.Cigna],
    ),
    ...getOrdersMocks.SINGLE_ORDER,
  ],
};

export const mocks: Record<string, Mock> = {
  HAPPY_PATH: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getOrdersMocks.SINGLE_ORDER, error: undefined },
  },
  HAPPY_PATH_ELIGIBILITY: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: [], error: undefined },
    getEligibilities: { data: getEligibilitiesMocks.SINGLE_UNCLAIMED },
  },
  INVALID_USER: {
    submit: submitMocks.SUBMIT_ERROR,
    getOrders: { data: undefined, error: 'Error fetching order' },
  },
  SUBMIT_ERROR: {
    submit: submitMocks.SUBMIT_ERROR,
    getOrders: { data: undefined, error: undefined },
  },
  ORDER_COMPLETE: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getOrdersMocks.COMPLETED_ORDER, error: undefined },
  },
  BUSINESS_ENROLL: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getOrdersMocks.ENROLL },
  },
  BUSINESS_ENROLL_NO_SIZING: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getOrdersMocks.ENROLL_NO_SIZING },
  },
  BUSINESS_SINGLE_ORDER: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getOrdersMocks.SINGLE_ORDER },
  },
  BUSINESS_SINGLE_COBRANDED_ORDER: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getOrdersMocks.SINGLE_COBRANDED_ORDER },
  },
  BUSINESS_SINGLE_ORDER_MISSING_ORDERID: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getOrdersMocks.SINGLE_ORDER_MISSING_ORDERID },
  },
  BUSINESS_MULTI_ORDER: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getOrdersMocks.MULTIPLE_ORDERS },
  },
  BUSINESS_MULTI_CHANNEL_ORDER: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getOrdersMocks.MULTIPLE_CHANNEL_ORDERS },
  },
  BUSINESS_MULTI_PAGES: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getOrdersMocks.MULTIPLE_PAGES },
  },
  BUSINESS_ORDER_COMPLETE: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getOrdersMocks.COMPLETED_ORDER },
  },
  CIGNA_SINGLE_ORDER: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getCignaMocks.SINGLE_ORDER },
  },
  CIGNA_MULTI_ORDER: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getCignaMocks.MULTIPLE_ORDERS },
  },
  CIGNA_MULTI_CHANNEL_ORDER: {
    submit: submitMocks.SUBMIT_SUCCESS,
    getOrders: { data: getCignaMocks.MULTI_CHANNEL_ORDERS },
  },
};

export const redeemMocks: Record<string, ValidateRedeemCodeResult> = {
  mock_free_sizing_kit: {
    success: true,
    data: {
      code: 'mock_free_sizing_kit',
      lineItems: [
        {
          title: 'Mock Sizing Kit',
          description: 'A mock sizing kit for testing purposes.',
          image: {
            src: '/mock-sizing-kit.png',
            alt: 'Mock Sizing Kit Image',
            width: 100,
            height: 100,
          },
        },
      ],
      customerId: 'mock_olympics',
      campaignId: 'mock_campaign',
      flow: {
        type: 'free_sizing_kit',
        meta: {},
      },
    },
  },
  mock_free_sizing_kit_plus_discount: {
    success: true,
    data: {
      code: 'mock_free_sizing_kit_plus_discount',
      lineItems: [
        {
          title: 'Mock Sizing Kit + Discount',
          description: 'A mock sizing kit with discount for testing purposes.',
          image: {
            src: '/mock-sizing-kit-discount.png',
            alt: 'Mock Sizing Kit + Discount Image',
            width: 100,
            height: 100,
          },
        },
      ],
      customerId: 'mock_customer',
      campaignId: 'mock_campaign',
      flow: {
        type: 'free_sizing_kit_plus_discount',
        meta: {
          discountPercent: '10',
        },
      },
    },
  },
};
